import React, { useState } from 'react';
import { pwStrength } from './vaultPassphraseUtils';
import './Vault.css';

/**
 * The passphrase field and its advisory strength meter, shared by every
 * master-passphrase screen (CONTRACT §6.9 "Reuse").
 *
 * Deliberately re-implemented here rather than exported out of
 * `SettingsModal.js`: that module is 3k lines of unrelated settings, it is not
 * this phase's territory, and the two copies are ~30 lines of markup over the
 * same `pw-input-wrap` / `privacy-password-input` / `pw-eye-btn` classes that
 * already exist in `src/index.css`. No Tailwind, no new classnames.
 *
 * The value lives in the caller's component state and nowhere else. Nothing
 * here writes it to storage, a log, an analytics call or an error string.
 */

const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4 20 20" />
    <path d="M9.6 5.9A9.4 9.4 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.7 3.4" />
    <path d="M6.3 7.8A15.7 15.7 0 0 0 2.5 12S6 18.5 12 18.5a9 9 0 0 0 3-.5" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

export default function VaultPassphraseInput({
  value,
  onChange,
  placeholder,
  autoComplete = 'new-password',
  disabled = false,
  autoFocus = false,
  label,
  id,
  onKeyDown,
}) {
  const [reveal, setReveal] = useState(false);
  return (
    <div>
      {label && (
        <label className="vault-pass-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="pw-input-wrap">
        <input
          id={id}
          type={reveal ? 'text' : 'password'}
          className="privacy-password-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={label || placeholder}
          autoComplete={autoComplete}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          className="pw-eye-btn"
          tabIndex={-1}
          onClick={() => setReveal((r) => !r)}
          aria-label={reveal ? 'Hide passphrase' : 'Show passphrase'}
        >
          {reveal ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}

const PW_STR_COLOR = ['', '#f3677e', '#f0a23b', '#e0c54a', '#4ade80'];
const PW_STR_LABEL = ['', 'Weak', 'Fair', 'Good', 'Strong'];

/**
 * Advisory only, never a gate (§5, Disagreement 6). `pwStrength` counts
 * character classes; it does not estimate guessability, so this renders a bar
 * and a one-word label and the copy beside it does the honest talking.
 */
export function VaultPassphraseStrength({ value }) {
  if (!value) return null;
  const s = pwStrength(value);
  return (
    <div className="vault-pass-strength">
      <div className="pw-strength-meter">
        {[1, 2, 3, 4].map((i) => (
          <i key={i} style={{ background: i <= s ? PW_STR_COLOR[s] : undefined }} />
        ))}
      </div>
      <span
        className="pw-strength-label"
        style={{ color: PW_STR_COLOR[s] || 'var(--text-3, #71717a)' }}
      >
        {PW_STR_LABEL[s] || 'Too short'}
      </span>
    </div>
  );
}
